import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Button,
  Select,
  Checkbox,
} from 'semantic-ui/semantic-ui-imports';
import './index.scss';
import { getProject, getProjects } from 'services/projects';
import { Project, Result } from 'Types/Project';
import { useParams } from 'react-router-dom';
import { toast } from 'react-semantic-toasts';
import { useAuth } from '../../../contexts/auth';
import { PromptToSignInContext } from '../../../contexts/promptToSignIn';

interface Props {
  initialProject?: Project;
}

function ReguestOffer({ initialProject }: Props) {
  const { t } = useTranslation();
  const { projectId, resultId } = useParams<{
    projectId: string;
    resultId: string;
  }>();

  const [projects, setProjects] = React.useState<Project[]>([]);
  const [selectedResultIds, setSelectedResultIds] = React.useState<
    Array<number | string>
  >([]);
  const [selectedProject, setSelectedProject] = React.useState<
    Project | undefined
  >(initialProject);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string>();
  /* const [name, setName] = React.useState<string>(); */
  const { promptToSignIn } = React.useContext(PromptToSignInContext);
  const { isAuthenticated } = useAuth();

  // Pre-populate message when loading a project with a requester_message
  // Watch selectedProject (not initialProject) because that's what changes when user picks from dropdown
  React.useEffect(() => {
    setMessage(selectedProject?.requester_message || '');
  }, [selectedProject?.requester_message]);

  // Pre-select results that are currently pending, plus the result from URL if provided
  // Use 'status' field - if backend accidentally sets quoted/declined, treat as offer_requested
  React.useEffect(() => {
    if (selectedProject?.results) {
      const pendingStatuses = ['offer_requested', 'quoted', 'declined'];
      const pendingIds = selectedProject.results
        .filter((r) => r.status && pendingStatuses.includes(r.status))
        .map((r) => r.id);

      // Also select the result from URL params if it's a valid calculated/withdrawn result
      if (resultId) {
        const resultFromUrl = selectedProject.results.find(
          (r) => String(r.id) === resultId
        );
        if (
          resultFromUrl &&
          !pendingIds.includes(resultFromUrl.id) &&
          (resultFromUrl.status === 'calculated' ||
            resultFromUrl.status === 'withdrawn')
        ) {
          pendingIds.push(resultFromUrl.id);
        }
      }

      setSelectedResultIds(pendingIds);
    } else {
      setSelectedResultIds([]);
    }
  }, [selectedProject, resultId]);

  const showError = () => {
    toast({
      type: 'error',
      title: t('resultsPage.requestOfferComponent.errorText'),
      time: 4000,
      size: 'small',
    });
  };

  const onSubmit = async () => {
    if (!isAuthenticated) {
      promptToSignIn();
      return;
    }

    setIsLoading(true);
    const params = {
      message,
      name: selectedProject?.name,
      project_id: selectedProject?.id,
      results: selectedResultIds,
    };

    try {
      const { data } = await axios.post(`/results/offer/`, params);

      if (data.submitted === 0 && data.withdrawn === 0) {
        // Nothing changed - user didn't select any results
        toast({
          type: 'warning',
          title: t('resultsPage.requestOfferComponent.nothingToSubmit'),
          time: 4000,
          size: 'small',
        });
      } else if (data.submitted === 0) {
        // Only withdrawals, no new submissions
        toast({
          type: 'info',
          title: t('resultsPage.requestOfferComponent.requestWithdrawn'),
          time: 4000,
          size: 'small',
        });
      } else {
        // Submitted calculations
        toast({
          type: 'success',
          title: t('resultsPage.requestOfferComponent.offerSent', {
            count: data.submitted,
            name: data.name,
          }),
          time: 4000,
          size: 'small',
        });
      }

      // Reload the project to sync UI with database
      // Handles edge case where partner changed status while user had form open
      if (selectedProject?.id) {
        const refreshedProject = await getProject(selectedProject.id);
        if (refreshedProject) {
          setSelectedProject(refreshedProject);
        }
      }
    } catch (error) {
      showError();
    }
    setIsLoading(false);
  };

  const getPjs = async () => {
    setIsLoading(true);
    try {
      const ps = await getProjects();
      if (ps?.results) {
        setProjects(ps.results);
      }
    } catch (err) {
      showError();
    }
    setIsLoading(false);
  };

  const getPj = async (id: string | number) => {
    if (id === selectedProject?.id) {
      return;
    }
    setIsLoading(true);
    try {
      const p = await getProject(id);
      if (p) {
        setSelectedProject(p);
      }
    } catch (err) {
      showError();
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    getPjs();
  }, []);

  React.useEffect(() => {
    if (!initialProject && projectId) {
      getPj(projectId);
    }
  }, [projectId]);

  const toggleResult = (checked: boolean, id: string | number) => {
    let ids = selectedResultIds;
    if (checked && !selectedResultIds.includes(id)) {
      ids.push(id);
    } else if (!checked) {
      ids = ids.filter((x) => x !== id);
    }
    setSelectedResultIds([...ids]);
  };

  const classId = 'request-offer';
  return (
    <div className={classId}>
      <h1>{t('resultsPage.requestOfferComponent.title')}</h1>
      <div className="request-offer__inner">
        <Form className="reguest-offer__form" onSubmit={onSubmit}>
          {/* <Form.Input
            className="request-offer__name"
            placeholder={t('resultsPage.requestOfferComponent.namePlaceholder')}
            onChange={(e: any, { value }: any) => setName(value?.toString())}
            label={t('resultsPage.requestOfferComponent.nameLabel')}
            fluid
          /> */}
          <Form.Field
            control={Select}
            className={`${classId}__project__input`}
            options={
              projects && projects.length
                ? projects.map((p) => ({
                    key: p.id,
                    text: p.name,
                    value: p.id,
                  }))
                : []
            }
            label={t('saveToProject.projectPlaceholder')}
            loading={isLoading}
            defaultValue={initialProject?.id}
            onChange={(ev: any, pEv: any) => {
              getPj(pEv.value);
            }}
            required
          />

          {!!selectedProject?.results?.length && (
            <div className={`${classId}__results-input`}>
              {' '}
              {selectedProject?.results.map((r: Result) => {
                // Use effective_status to determine if checkbox should be locked
                // Lock when: draft (not ready), selected (not calculated yet), quoted (partner responded), declined (partner declined)
                const lockedStatuses = [
                  'draft',
                  'selected',
                  'quoted',
                  'declined',
                ];
                const isLocked =
                  r.effective_status &&
                  lockedStatuses.includes(r.effective_status);
                return (
                  <Checkbox
                    key={r.id}
                    id={r.id}
                    type="checkbox"
                    label={r.name}
                    name={r.name}
                    value={r.id}
                    checked={selectedResultIds.includes(r.id)}
                    disabled={isLocked}
                    onChange={(e: any) => {
                      toggleResult(e.target.checked, r.id);
                    }}
                  />
                );
              })}
            </div>
          )}

          <Form.TextArea
            className="request-offer__message"
            placeholder={t(
              'resultsPage.requestOfferComponent.messagePlaceholder'
            )}
            value={message || ''}
            onChange={(e: any, { value }: any) => setMessage(value?.toString())}
            label={t('resultsPage.requestOfferComponent.messageLabel')}
          />
          <Button
            className="request-offer__button"
            type="submit"
            color="teal"
            fluid
            size="large"
            disabled={isLoading}
            loading={isLoading}
          >
            {t('resultsPage.requestOfferComponent.buttonText')}
          </Button>
        </Form>
      </div>
    </div>
  );
}

export default ReguestOffer;
